import { useRef, useState, useCallback } from 'react';
import {
  alpha,
  Box,
  Button,
  Fade,
  LinearProgress,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { NOTIFICATION_GENERATED_GRID_APP_PATH } from './notificationGeneratedGrid';
import NotificationHeader from './components/NotificationHeader';
import NotificationsList from './components/NotificationsList';
import NotificationTrigger from './components/NotificationTrigger';
import MessageModal from './components/MessageModal';
import { useNotificationState } from './hooks/useNotificationState';

interface NotificationProps {
  openButtonLocalization: string;
  closeButtonLocalization: string;
}

const NOTIFICATIONS_POPOVER_TRANSITION_MS = { enter: 340, exit: 200 } as const;

const NOTIFICATIONS_POPOVER_EASING = {
  enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
  exit: 'cubic-bezier(0.32, 0, 0.67, 0)',
} as const;

const Notifications: React.FC<NotificationProps> = (props) => {
  const {
    notificationGridContentKey,
    listEnded,
    listScrollResetToken,
    notifications,
    unreadMessages,
    unreadCount,
    isOpened,
    showOnlyUnread,
    isFilterChanging,
    isNotificationsInitialLoad,
    loading,
    notificationSettings,
    setIsOpened,
    fetchNotifications,
    handleReadAllNotifications,
    setShowOnlyUnread,
    setIsReadNotification,
    setUnreadMessages,
    openButtonLocalization,
    closeButtonLocalization,
  } = useNotificationState(props);

  const navigate = useNavigate();
  const openBtnRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [bottomAtScrollToken, setBottomAtScrollToken] = useState<number | null>(null);
  const isListFullyScrolled =
    bottomAtScrollToken !== null && bottomAtScrollToken === listScrollResetToken;

  const handleUnreadOnlyToggle = useCallback(() => {
    if (isFilterChanging) {
      return;
    }
    setShowOnlyUnread((prev) => !prev);
  }, [isFilterChanging, setShowOnlyUnread]);

  const openNotification = () => {
    setIsOpened((prev) => {
      const next = !prev;
      setAnchorEl(next ? openBtnRef.current : null);
      return next;
    });
  };

  const handlePopoverClose = (
    _event: unknown,
    reason: 'backdropClick' | 'escapeKeyDown' = 'backdropClick'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      setIsOpened(false);
      setAnchorEl(null);
      openBtnRef.current?.focus();
    }
  };

  const onReadAllClick = () => {
    if (unreadCount > 0) {
      handleReadAllNotifications();
    }
  };

  const onSeeAllClick = () => {
    if (!notificationGridContentKey) {
      return;
    }
    setIsOpened(false);
    navigate({ to: NOTIFICATION_GENERATED_GRID_APP_PATH });
  };

  const areCurrentNotificationsEmpty = notifications.length === 0;
  const listEmptyWhileFetching = loading && areCurrentNotificationsEmpty;
  const shouldShowEndMessage = isListFullyScrolled || (areCurrentNotificationsEmpty && !loading);
  const showListBusy = isNotificationsInitialLoad || isFilterChanging || listEmptyWhileFetching;

  const handleScrolledToBottomChange = useCallback(
    (isScrolledToBottom: boolean) => {
      if (isScrolledToBottom) {
        setBottomAtScrollToken(listScrollResetToken);
        if (!loading && !listEnded && !isFilterChanging) {
          fetchNotifications(false);
        }
      }
    },
    [loading, listEnded, isFilterChanging, fetchNotifications, listScrollResetToken]
  );

  return (
    <>
      <MessageModal
        unreadMessages={unreadMessages}
        setUnreadMessages={setUnreadMessages}
        setIsReadNotification={setIsReadNotification}
        openButtonLocalization={openButtonLocalization}
        closeButtonLocalization={closeButtonLocalization}
      />

      <NotificationTrigger
        ref={openBtnRef}
        openNotification={openNotification}
        unreadCount={unreadCount}
      />

      <Popover
        open={isOpened}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        transitionDuration={NOTIFICATIONS_POPOVER_TRANSITION_MS}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              maxHeight: 'min(600px, 85vh)',
              width: 400,
              maxWidth: 'min(400px, 92vw)',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: `0 16px 52px ${alpha('#0f172a', 0.2)}, 0 4px 14px ${alpha('#0f172a', 0.08)}`,
            },
          },
          transition: {
            easing: NOTIFICATIONS_POPOVER_EASING,
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundImage: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <NotificationHeader
            showOnlyUnread={showOnlyUnread || false}
            handleUnreadOnlyToggle={handleUnreadOnlyToggle}
          />

          <Box sx={{ position: 'relative' }}>
            <Fade in={showListBusy} timeout={{ enter: 320, exit: 480 }} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              >
                <LinearProgress
                  color="primary"
                  sx={{
                    height: 2,
                    borderRadius: 0,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    '& .MuiLinearProgress-bar': { borderRadius: 0 },
                  }}
                />
              </Box>
            </Fade>
            <NotificationsList
              notifications={notifications}
              setIsReadNotification={setIsReadNotification}
              onScrolledToBottomChange={handleScrolledToBottomChange}
              listEmptyWhileFetching={listEmptyWhileFetching}
            />
          </Box>

          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="column" spacing={1.25}>
              {shouldShowEndMessage && notificationSettings?.DisplayPeriod != null && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ lineHeight: 1.45, px: 0.5 }}
                >
                  That&apos;s all your notifications from the last{' '}
                  {notificationSettings.DisplayPeriod} days.
                </Typography>
              )}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="text"
                  size="small"
                  fullWidth
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    py: 0.75,
                    color: 'text.primary',
                  }}
                  disabled={!notificationGridContentKey}
                  onClick={onSeeAllClick}
                >
                  See All
                </Button>
                <Button
                  variant="text"
                  size="small"
                  fullWidth
                  onClick={onReadAllClick}
                  disabled={unreadCount === 0}
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    py: 0.75,
                  }}
                >
                  Read All
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default Notifications;
